import { useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { ContactUsBanner } from '../../components/contactUs/ContactUsBanner'
import { Form } from '../../components/form/Form'
import { BannerButton, BannerButtonProps } from '../../elements/BannerButton'
import { Heading } from '../../elements/Heading'
import { useAppData } from '../../hooks/useAppData'
import { setSelectedForm } from '../../redux/contactUsSlice'
import { useAppSelector } from '../../types/assets'
import { urlify } from '../../utils/common'
import { GlobalStyles } from '../Home/styles'
import { ContactUsSkeleton } from './ContactUsSkeleton'
import { StyledContactUs } from './styles'

export const ContactUs = () => {
  const dispatch = useDispatch()
  const params: { formDisplayName?: string | undefined } = useParams()
  const prevParams = useRef<{ formDisplayName: string | undefined }>({
    formDisplayName: undefined,
  })
  const state = useAppSelector(state => state)

  const {
    contactUs: {
      loading: contactUsLoading,
      contactUs: { defaultContactForm, additionalContactForms, selectedForm },
    },
    settings: {
      loading,
      config: { path },
      theme: { contactUsBannerSrc },
    },
  } = state

  useEffect(() => {
    if (!params.formDisplayName || params === prevParams.current) return

    const newSelectedForm = [defaultContactForm, ...(additionalContactForms ?? [])].find(contactForm => {
      if (!contactForm) return false

      return urlify(contactForm.displayLabel) === params.formDisplayName
    })

    if (selectedForm !== newSelectedForm) {
      dispatch(setSelectedForm(newSelectedForm))
    }
  }, [dispatch, params, defaultContactForm, additionalContactForms, selectedForm])

  const navigate = useNavigate()

  const bannerButtonProps: BannerButtonProps[] = useMemo(() => {
    return [defaultContactForm, ...(additionalContactForms ?? [])]
      .map(contactForm => {
        if (!contactForm) {
          return {
            to: `/contact`,
            label: '',
            onClick: () => {},
            activeButton: false,
          }
        }
        return {
          to: `/${path}/contact/${urlify(contactForm?.displayLabel)}`,
          label: contactForm.displayLabel,
          onClick: () => {
            dispatch(setSelectedForm(contactForm))
            navigate(`/${path}/contact/${urlify(contactForm?.displayLabel)}`)
          },
          activeButton: selectedForm?.formGuid === contactForm.formGuid,
        }
      })
      .filter(a => a.label !== '')
  }, [defaultContactForm, additionalContactForms, selectedForm?.formGuid, path, dispatch, navigate])

  const showLoader = loading === 'loading' || contactUsLoading === 'idle' || contactUsLoading === 'loading'

  const haveContactForms = !!bannerButtonProps.length

  const { updateAppData } = useAppData()

  useEffect(() => {
    updateAppData({})
  }, [])

  return (
    <>
      <StyledContactUs>
        <GlobalStyles greyColor />
        {showLoader ? (
          <ContactUsSkeleton imageSrc={contactUsBannerSrc ?? ''} />
        ) : (
          contactUsLoading === 'succeeded' && (
            <>
              <ContactUsBanner
                showHeader={bannerButtonProps.length > 1}
                bannerImageSrc={contactUsBannerSrc ?? ''}
                contactUsInstructionText={selectedForm?.contactInstructionText ?? ''}
              >
                {haveContactForms ? (
                  bannerButtonProps.map((props, index) => (
                    <BannerButton
                      key={index}
                      {...props}
                    />
                  ))
                ) : (
                  <Heading size="L">No Contact Forms are setup</Heading>
                )}
              </ContactUsBanner>
              <Form
                formGuid={selectedForm?.formGuid}
                isContact={true}
              />
            </>
          )
        )}
      </StyledContactUs>
    </>
  )
}
